import { create } from 'zustand';
import { WidgetsApi, type CustomTab, type NegotiationWidget } from '../api/widgets';

type CustomDashboardState = {
  customTabs: CustomTab[];
  loadingCustomTabs: boolean;
  widgets: NegotiationWidget[];
  widgetsLoading: boolean;
  widgetsError: string | null;
  loadCustomTabs: (negotiationId: number) => Promise<void>;
  createCustomTab: (negotiationId: number, pageName: string) => Promise<CustomTab | null>;
  deleteCustomTab: (tabId: number) => Promise<boolean>;
  loadWidgets: (negotiationId: number, pageName: string) => Promise<void>;
  removeWidget: (widgetId: number) => Promise<void>;
  saveWidget: (payload: {
    negotiationId: number;
    pageName: string;
    componentType: string;
    chartVegajson: any;
    chartDescription?: string;
  }) => Promise<NegotiationWidget | null>;
  reset: () => void;
};

export const useCustomDashboardStore = create<CustomDashboardState>((set, get) => ({
  customTabs: [],
  loadingCustomTabs: false,
  widgets: [],
  widgetsLoading: false,
  widgetsError: null,

  loadCustomTabs: async (negotiationId: number) => {
    set({ loadingCustomTabs: true });
    try {
      const data = await WidgetsApi.listCustomTabs(negotiationId);
      set({ customTabs: data ?? [] });
    } catch (e) {
      console.error(e);
      set({ customTabs: [] });
    } finally {
      set({ loadingCustomTabs: false });
    }
  },

  createCustomTab: async (negotiationId: number, pageName: string) => {
    try {
      const created = await WidgetsApi.createCustomTab(negotiationId, pageName);
      set({ customTabs: [...get().customTabs, created] });
      return created;
    } catch (e) {
      console.error(e);
      return null;
    }
  },

  deleteCustomTab: async (tabId: number) => {
    try {
      await WidgetsApi.deleteCustomTab(tabId);
      set({ customTabs: get().customTabs.filter((t) => t.id !== tabId) });
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  },

  loadWidgets: async (negotiationId: number, pageName: string) => {
    set({ widgetsLoading: true, widgetsError: null });
    try {
      const data = await WidgetsApi.listWidgets(negotiationId, pageName);
      set({ widgets: data ?? [] });
    } catch (e: any) {
      set({ widgets: [], widgetsError: e?.message || 'Unable to load widgets' });
    } finally {
      set({ widgetsLoading: false });
    }
  },

  removeWidget: async (widgetId: number) => {
    try {
      await WidgetsApi.removeWidget(widgetId);
      set({ widgets: get().widgets.filter((w) => w.negotiation_report_chart_id !== widgetId) });
    } catch (e: any) {
      set({ widgetsError: e?.message || 'Unable to remove widget' });
    }
  },

  saveWidget: async ({ negotiationId, pageName, componentType, chartVegajson, chartDescription }) => {
    try {
      const created = await WidgetsApi.createWidget({
        negotiation_id: negotiationId,
        page_name: pageName,
        component_type: componentType,
        chart_vegajson: chartVegajson,
        chart_description: chartDescription,
        is_display: true,
      });
      set({ widgets: [...get().widgets, created] });
      return created;
    } catch (e) {
      console.error(e);
      return null;
    }
  },

  reset: () => set({
    customTabs: [],
    loadingCustomTabs: false,
    widgets: [],
    widgetsLoading: false,
    widgetsError: null,
  }),
}));
